/*
 * Presentación del incremento 3/6.
 *
 * Los importes viajan como CADENA DECIMAL, nunca como number (RNF-001): aquí solo
 * se les da formato para mostrarlos y JAMÁS se opera con ellos. Todo cálculo —el
 * saldo, la deuda, el disponible y los totales— lo hace el servidor (D-24,
 * RNF-030); si esta pantalla sumara, habría dos verdades.
 *
 * Tampoco se calculan fechas: la fecha de negocio la resuelve el dispositivo con
 * el contrato del núcleo (D-06) y el periodo lo deriva el servidor (RN-075b).
 */

import { TIPOS } from './api/movimientosApi.js';

/*
 * BARRIDO D-88 / RN-147: el formato de importe es uno solo y vive en
 * `nucleo/importes/formatoDeImporte.js`. El segundo argumento es el CÓDIGO de la
 * divisa (`MXN` / `USD`), no el símbolo. El signo menos de los importes calculados
 * lo pone el formato único, antes del `$` y sin espacio (RN-146e).
 */
export { formatearImporte as importe } from '../../nucleo/importes/formatoDeImporte.js';

/**
 * Cómo se presenta el importe de cada tipo. EL SIGNO LO DA EL TIPO, NUNCA EL
 * NÚMERO: en la base todos los importes son positivos (RN-074).
 *
 * La DEVOLUCIÓN se pinta con su propio color y su propia leyenda porque no es un
 * ingreso: es gasto que se deshace (D-47, RN-095).
 *
 * IMPORTE LIMPIO · CATÁLOGO EXACTO DE RN-344 (H-13, RF-138, incremento 08).
 * La leyenda ya NO repite nada que el importe o el título del renglón digan:
 *   · el código de divisa (MXN / USD) · ELIMINADO: va dentro del importe
 *     formateado («$11,200.00 MXN»), y repetirlo producía «−$100.00 MXNMXN»;
 *   · «traspaso» · ELIMINADO: el título del renglón ya dice Traspaso, Pago de
 *     tarjeta o Disposición de efectivo (ver tituloDelMovimiento), y repetirlo
 *     producía «$11,200.00 MXNtraspaso»;
 *   · «sube la deuda», «baja la deuda», «baja el gasto» · SE CONSERVAN: dicen
 *     algo que el importe no dice.
 * Cuando no hay leyenda que mostrar, se devuelve `null` y la pantalla NO dibuja
 * el elemento vacío ni reserva el espacio (RN-346).
 */
export function presentacionDelImporte(movimiento) {
  switch (movimiento.tipo) {
    case TIPOS.gasto:
      return { signo: '−', clase: 'gasto', leyenda: movimiento.cuentaEsCredito ? 'sube la deuda' : null };
    case TIPOS.ingreso:
      return { signo: '+', clase: 'ingreso', leyenda: null };
    case TIPOS.devolucion:
      return { signo: '+', clase: 'devolucion', leyenda: movimiento.cuentaEsCredito ? 'baja la deuda' : 'baja el gasto' };
    case TIPOS.traspaso:
      return {
        signo: '',
        clase: 'traspaso',
        leyenda: movimiento.esDisposicionDeEfectivo ? 'sube la deuda' : null,
      };
    default:
      return { signo: '', clase: 'traspaso', leyenda: null };
  }
}

/**
 * RF-215 / RN-614 (D-254) · COLOR DEL IMPORTE POR TIPO, EN UN SOLO SITIO.
 *
 * `presentacionDelImporte` ya decidía la clase desde el 3/6, pero el marcado nunca
 * la usaba: todos los importes del historial se pintaban iguales, que es el defecto
 * de producción que Deisy Paez reportó. Esta tabla traduce esa clase a la clase CSS
 * de color —`.imp.g` rojo, `.imp.i` verde, `.imp.t` azul, todas por tokens— y la
 * comparten el historial del 3/6 y la tarjeta «Últimos movimientos» del Tablero,
 * para que no existan dos criterios de color del mismo dato (RN-600).
 *
 * La DEVOLUCIÓN comparte el verde del ingreso, con su «+» y su propio distintivo:
 * el color no es lo único que la distingue (RN-614).
 */
export const CLASE_DE_COLOR_DEL_IMPORTE = Object.freeze({
  gasto: 'g',
  ingreso: 'i',
  devolucion: 'i',
  traspaso: 't',
});

/** Clase CSS de color del importe de un movimiento. Cadena vacía si no aplica. */
export function claseDeColorDelImporte(movimiento) {
  return CLASE_DE_COLOR_DEL_IMPORTE[presentacionDelImporte(movimiento).clase] ?? '';
}

/**
 * Los TRES NOMBRES del traspaso en pantalla (RF-050). Un mismo movimiento, tres
 * nombres y ninguna diferencia en el modelo: el servidor ya calculó
 * `esPagoDeTarjeta` y `esDisposicionDeEfectivo`; la pantalla NO los deduce.
 */
export function tituloDelMovimiento(movimiento) {
  // [AMPLIADO · incremento 19/21 · D-425, RN-736c] EL RENGLÓN DE UN PLAN A MESES EN
  // CURSO no tiene gasto capturado detrás, así que no tiene ni categoría ni nota de
  // la que salga su descripción: lo nombra el `textoIdentificacion` que el servidor
  // ya armó —«Compra a meses del DD/MM/AAAA» (RN-732)— y que ocupa aquí el sitio de
  // la descripción. La pantalla NO lo compone ni lo concatena (RNF-038, RNF-130).
  // En todos los demás renglones llega nulo y esta función se comporta igual que
  // siempre: ni una pantalla cambia.
  if (movimiento.textoIdentificacion) return movimiento.textoIdentificacion;
  if (movimiento.tipo !== TIPOS.traspaso) return movimiento.nombreCategoria ?? movimiento.nombreTipo;
  if (movimiento.esPagoDeTarjeta) return 'Pago de tarjeta';
  if (movimiento.esDisposicionDeEfectivo) return 'Disposición de efectivo';
  return 'Traspaso';
}

/** Agrupa por fecha CONSERVANDO el orden que ya trae el servidor (RF-053). */
export function agruparPorDia(movimientos) {
  const dias = [];
  movimientos.forEach((movimiento) => {
    let dia = dias.find((d) => d.fecha === movimiento.fecha);
    if (!dia) {
      dia = { fecha: movimiento.fecha, movimientos: [] };
      dias.push(dia);
    }
    dia.movimientos.push(movimiento);
  });
  return dias;
}

/** Primer día del mes de una fecha de negocio, en formato AAAA-MM-DD. */
export function primerDiaDelMes(fechaDeNegocio) {
  const [anio, mes] = fechaDeNegocio.split('-');
  return `${anio}-${mes}-01`;
}

export const NOMBRE_DE_MONEDA = { MXN: 'Pesos mexicanos', USD: 'Dólares' };

/**
 * Texto fijo de ayuda del formulario de INGRESO (RN-096d). Se muestra SIEMPRE,
 * elijas la categoría que elijas: la pantalla NO inspecciona qué categoría se
 * marcó. Quien decide si además hay que avisar por la categoría "Reembolsos" es
 * el SERVIDOR, con la clave de siembra (D-50, RN-096).
 */
export const AYUDA_DE_DEVOLUCION =
  '¿Te devolvieron una compra que ya registraste? Regístrala como Devolución para que se corrija ese gasto.';
